import React, { useState, useContext, useRef, useEffect } from 'react';
import { AiOutlineClose, AiOutlinePlusCircle } from "react-icons/ai";
import Modal from 'react-modal';
import Swal from 'sweetalert2';

import clienteAxios from '../../config/axios';
import { CRMContext } from '../context/CRMContext';

Modal.setAppElement('#root');

const slugClave = (nombre) => {
    const slug = String(nombre || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 191);

    return slug || `item-${Date.now()}`;
};

function FormNuevoContenido({actualizarContenido, activo}) {

    const [auth] = useContext(CRMContext);

    const [ contenido, guardarContenido ] = useState({
        nombre: '',
        descripcion: '',
        cantidad: '1',
        valor: '',
    });
    const [modalIsOpen, setIsOpen] = useState(false);
    const [ sugerencias, guardarSugerencias ] = useState([]);
    const [ mostrarLista, guardarMostrarLista ] = useState(false);
    const [ buscando, guardarBuscando ] = useState(false);
    const [ itemCatalogo, guardarItemCatalogo ] = useState(null);
    const [ enviando, guardarEnviando ] = useState(false);
    const debounceRef = useRef(null);
    const cajaRef = useRef(null);

    const valorNumero = (numero) => new Intl.NumberFormat().format(numero);

    function openModal() {
        setIsOpen(true);
    }

    function closeModal() {
        setIsOpen(false);
        guardarMostrarLista(false);
        guardarSugerencias([]);
    }

    const resetForm = () => {
        guardarContenido({
            nombre: '',
            descripcion: '',
            cantidad: '1',
            valor: '',
        });
        guardarItemCatalogo(null);
        guardarSugerencias([]);
        guardarMostrarLista(false);
    }

    const listaDesdeRespuesta = (data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.resultados)) return data.resultados;
        return [];
    };

    const buscarItems = async (texto) => {
        const query = texto.trim();
        if (query.length < 2) {
            guardarSugerencias([]);
            return;
        }

        try {
            guardarBuscando(true);
            const res = await clienteAxios.get(`/cotizacion/items/buscar/${encodeURIComponent(query)}`, {
                headers: {
                    Authorization: `Bearer ${auth.token}`
                }
            });
            guardarSugerencias(listaDesdeRespuesta(res.data));
        } catch (error) {
            console.log(error);
            guardarSugerencias([]);
        } finally {
            guardarBuscando(false);
        }
    };

    const actualizarState = e => {
        const { name, value } = e.target;

        guardarContenido({
            ...contenido,
            [name]: value
        });

        if (name === 'nombre') {
            guardarItemCatalogo(null);
            guardarMostrarLista(true);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => buscarItems(value), 300);
        }
    };

    const seleccionarItem = (item) => {
        guardarItemCatalogo(item);
        guardarContenido({
            ...contenido,
            nombre: item.nombre || '',
            descripcion: item.descripcion || '',
            valor: String(item.valorActual ?? item.valor ?? '')
        });
        guardarMostrarLista(false);
    };

    const coincideExacto = (lista, nombre) => {
        const texto = nombre.trim().toLowerCase();
        return lista.find((item) => String(item.nombre || '').trim().toLowerCase() === texto);
    };

    const validarForm = () => {
        const {nombre, cantidad, valor} = contenido;

        if( nombre.length > 0 && cantidad.length > 0 && valor.length > 0 && !enviando ) {
            return false;
        }

        return true;
    }

    const enviarInfo = async (e) => {
        e.preventDefault();

        if (enviando) {
            return;
        }

        guardarEnviando(true);

        const existente = itemCatalogo || coincideExacto(sugerencias, contenido.nombre);

        if (!existente) {
            try {
                await clienteAxios.post('/cotizacion/items', {
                    nombre: contenido.nombre.trim(),
                    descripcion: contenido.descripcion || '',
                    claveUnica: slugClave(contenido.nombre),
                    valorActual: parseInt(contenido.valor, 10) || 0,
                    fechaValor: new Date().toISOString().slice(0, 10),
                    activo: true
                }, {
                    headers: {
                        Authorization: `Bearer ${auth.token}`
                    }
                });
            } catch (error) {
                console.log(error);
                Swal.fire({
                    type: 'warning',
                    title: 'Se agregó a la cotización',
                    text: error.response?.data?.msg || 'No se pudo guardar el ítem en el catálogo. Quedó igual como línea de esta cotización.',
                    timer: 2500
                });
            }
        }

        closeModal();
        actualizarContenido(contenido);
        resetForm();
        guardarEnviando(false);
    }

    const noHayCoincidenciaExacta = contenido.nombre.trim().length >= 2
        && !coincideExacto(sugerencias, contenido.nombre)
        && !itemCatalogo;

    useEffect(() => {
        const cerrarLista = (e) => {
            if (cajaRef.current && !cajaRef.current.contains(e.target)) {
                guardarMostrarLista(false);
            }
        };
        document.addEventListener('mousedown', cerrarLista);
        return () => {
            document.removeEventListener('mousedown', cerrarLista);
            clearTimeout(debounceRef.current);
        };
    }, []);

    return (
        <div>
            <button className={activo ? 'btn-new' : 'btn-new btn-success-new'} onClick={openModal} disabled={activo}><AiOutlinePlusCircle size={20}/> Nuevo Contenido</button>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                className="Modal"
                contentLabel="Example Modal"
                overlayClassName="Overlay"
            >

                <p onClick={closeModal} className="modal__close"><AiOutlineClose/></p> 
                <h2 className='modal__titulo'>Ingrese el nuevo cobro</h2>
                
                <form onSubmit={enviarInfo} autoComplete="off">
                    <div className='campo' ref={cajaRef}>
                        <label htmlFor="nombre">Nombre<span className='campo__obligatorio'>*</span>:</label>
                        <div className="item-autocomplete">
                            <input 
                                type="text" 
                                id='nombre'
                                name='nombre'
                                placeholder='Escribe para buscar en el catálogo'
                                value={contenido.nombre}
                                onChange={actualizarState}
                                onFocus={() => contenido.nombre.trim().length >= 2 && guardarMostrarLista(true)}
                                autoComplete="off"
                            />
                            {
                                mostrarLista ? (
                                    <ul className="item-autocomplete__lista">
                                        {
                                            buscando ? (
                                                <li className="item-autocomplete__vacio">Buscando...</li>
                                            ) : null
                                        }
                                        {
                                            sugerencias.map((item) => (
                                                <li key={item.id || item.claveUnica}>
                                                    <button type="button" onClick={() => seleccionarItem(item)}>
                                                        <span className="item-autocomplete__nombre">{item.nombre}</span>
                                                        <span className="item-autocomplete__meta">
                                                            {item.descripcion ? `${item.descripcion} · ` : ''}
                                                            ${valorNumero(item.valorActual ?? item.valor ?? 0)}
                                                        </span>
                                                    </button>
                                                </li>
                                            ))
                                        }
                                        {
                                            !buscando && sugerencias.length === 0 && contenido.nombre.trim().length >= 2 ? (
                                                <li className="item-autocomplete__vacio">Sin coincidencias en el catálogo</li>
                                            ) : null
                                        }
                                        {
                                            noHayCoincidenciaExacta ? (
                                                <li>
                                                    <button
                                                        type="button"
                                                        className="item-autocomplete__crear"
                                                        onClick={() => guardarMostrarLista(false)}
                                                    >
                                                        Crear «{contenido.nombre.trim()}»
                                                    </button>
                                                </li>
                                            ) : null
                                        }
                                    </ul>
                                ) : null
                            }
                        </div>
                    </div>
                    <p className="item-autocomplete__ayuda">
                        {itemCatalogo
                            ? 'Ítem tomado del catálogo. Puedes ajustar cantidad o valor.'
                            : 'Escribe para filtrar. Si no existe, se creará en el catálogo al guardar.'}
                    </p>

                    <div className='campo'>
                        <label htmlFor="descripcion">Descripción:</label>
                        <input 
                            type="text" 
                            id='descripcion'
                            name='descripcion'
                            placeholder='Descripción del Componente'
                            value={contenido.descripcion}
                            onChange={actualizarState}
                        />
                    </div>

                    <div className='campo'>
                        <label htmlFor="cantidad">Cantidad<span className='campo__obligatorio'>*</span>:</label>
                        <input 
                            type="number" 
                            id='cantidad'
                            name='cantidad'
                            placeholder='Cantidad del Componente'
                            min="1"
                            value={contenido.cantidad}
                            onChange={actualizarState}
                        />
                    </div>

                    <div className='campo'>
                        <label htmlFor="valor">Valor<span className='campo__obligatorio'>*</span>:</label>
                        <input 
                            type="number" 
                            id='valor'
                            min="0"
                            name='valor'
                            placeholder='Valor del Componente'
                            value={contenido.valor}
                            onChange={actualizarState}
                        />
                    </div>

                    <div className="enviar">
                        <input 
                            type="submit" 
                            className={ validarForm() ? "btn-new"  : 'btn-new btn-success-new'}
                            value={itemCatalogo ? "Agregar cobro" : "Crear y agregar cobro"}
                            disabled={validarForm()}
                        />
                    </div>
                </form>                
            </Modal>
        </div>
    )
}

export default FormNuevoContenido;
